import { TaskOrTemplatePayload } from 'aos-tasks'

import { TaskOrTemplate } from '../task/Task'
import { TaskCategory } from '../TaskCategory'
import { TaskType } from '../TaskType'
import {
    AnimalsTaskFormPayload,
    animalsTaskFormPayloadFromTask,
    AnimalsTaskPayloadValidation,
    initialAnimalsTaskPayload,
    validateAnimalsTask,
} from './AnimalsTaskFormPayload'
import { baseTaskFormPayloadFromTask } from './BaseTaskFormPayload'
import {
    DeIcingTaskFormPayload,
    deIcingTaskFormPayloadFromTask,
    DeIcingTaskPayloadValidation,
    initialDeIcingTaskPayload,
    validateDeIcingTask,
} from './DeIcingTaskFormPayload'
import {
    initialInspectionTaskPayload,
    InspectionTaskFormPayload,
    inspectionTaskFormPayloadFromTask,
    InspectionTaskPayloadValidation,
    validateInspectionTask,
} from './InspectionTaskFormPayload'
import {
    initialMaintenanceTaskPayload,
    MaintenanceTaskFormPayload,
    maintenanceTaskFormPayloadFromTask,
    MaintenanceTaskPayloadValidation,
    validateMaintenanceTask,
} from './MaintenanceTaskFormPayload'
import {
    initialRefuellingTaskPayload,
    RefuellingTaskFormPayload,
    refuellingTaskFormPayloadFromTask,
    RefuellingTaskPayloadValidation,
    validateRefuellingTask,
} from './RefuellingTaskFormPayload'
import {
    initialRescueServiceTaskPayload,
    RescueServiceTaskFormPayload,
    rescueServiceTaskFormPayloadFromTask,
    RescueServiceTaskPayloadValidation,
    validateRescueServiceTask,
} from './RescueServiceTaskFormPayload'
import {
    initialSimpleTaskPayload,
    SimpleTaskFormPayload,
    simpleTaskFormPayloadFromTask,
    SimpleTaskPayloadValidation,
    validateSimpleTask,
} from './SimpleTaskFormPayload'
import {
    initialTerminalTaskPayload,
    TerminalTaskFormPayload,
    terminalTaskFormPayloadFromTask,
    TerminalTaskPayloadValidation,
    validateTerminalTask,
} from './TerminalTaskFormPayload'

export type TaskActionPayload = {
    payload: TaskFormPayload
    createAnother: boolean
    onSuccess?: () => void
}

export type TaskFormPayload =
    | SimpleTaskFormPayload
    | AnimalsTaskFormPayload
    | DeIcingTaskFormPayload
    | MaintenanceTaskFormPayload
    | InspectionTaskFormPayload
    | RescueServiceTaskFormPayload
    | RefuellingTaskFormPayload
    | TerminalTaskFormPayload

export type TaskPayloadValidation =
    | SimpleTaskPayloadValidation
    | AnimalsTaskPayloadValidation
    | DeIcingTaskPayloadValidation
    | MaintenanceTaskPayloadValidation
    | InspectionTaskPayloadValidation
    | RescueServiceTaskPayloadValidation
    | RefuellingTaskPayloadValidation
    | TerminalTaskPayloadValidation

export const validateTaskPayload = (v: TaskFormPayload): TaskPayloadValidation => {
    switch (v.type) {
        case TaskType.Simple:
            return validateSimpleTask(v)
        case TaskType.AnimalPrevention:
            return validateAnimalsTask(v)
        case TaskType.DeIcing:
            return validateDeIcingTask(v)
        case TaskType.Maintenance:
            return validateMaintenanceTask(v)
        case TaskType.RescueService:
            return validateRescueServiceTask(v)
        case TaskType.Inspection:
            return validateInspectionTask(v)
        case TaskType.Refuelling:
            return validateRefuellingTask(v)
        case TaskType.Terminal:
            return validateTerminalTask(v)
    }
}

export const taskPayloadFromTask = (v: TaskOrTemplate, isClone = false): TaskFormPayload => {
    switch (v.type) {
        case TaskType.Simple:
            return simpleTaskFormPayloadFromTask(v, isClone)
        case TaskType.AnimalPrevention:
            return animalsTaskFormPayloadFromTask(v, isClone)
        case TaskType.DeIcing:
            return deIcingTaskFormPayloadFromTask(v, isClone)
        case TaskType.Maintenance:
            return maintenanceTaskFormPayloadFromTask(v, isClone)
        case TaskType.RescueService:
            return rescueServiceTaskFormPayloadFromTask(v, isClone)
        case TaskType.Inspection:
            return inspectionTaskFormPayloadFromTask(v, isClone)
        case TaskType.Refuelling:
            return refuellingTaskFormPayloadFromTask(v, isClone)
        case TaskType.Terminal:
            return terminalTaskFormPayloadFromTask(v, isClone)
        default:
            return simpleTaskFormPayloadFromTask(v, isClone)
    }
}

export const initialPayloadForCategory: Record<TaskCategory, TaskFormPayload> = {
    [TaskCategory.Animals]: initialAnimalsTaskPayload,
    [TaskCategory.Maintenance]: initialMaintenanceTaskPayload,
    [TaskCategory.DeIcing]: initialDeIcingTaskPayload,
    [TaskCategory.RescueService]: initialRescueServiceTaskPayload,
    [TaskCategory.Inspection]: initialInspectionTaskPayload,
    [TaskCategory.LVP]: initialSimpleTaskPayload,
    [TaskCategory.HBA]: initialSimpleTaskPayload,
    [TaskCategory.Refuelling]: initialRefuellingTaskPayload,
    [TaskCategory.Terminal]: initialTerminalTaskPayload,
}

export const migrateTaskPayload = (
    from: TaskOrTemplatePayload,
    to: TaskOrTemplatePayload,
): TaskOrTemplatePayload => {
    if (from.category === to.category) {
        return to
    }
    const initial = to.category ? initialPayloadForCategory[to.category] : initialSimpleTaskPayload
    const base = baseTaskFormPayloadFromTask(to)
    const migrated = {
        ...initial,
        ...base,
    }
    return 'templateName' in to
        ? ({ ...migrated, templateName: to.templateName } as TaskOrTemplatePayload)
        : migrated
}
